import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  Input,
  OnInit,
  signal,
  ViewChild,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthorizationService } from '@nimmsta/ng-commons-lib/auth';
import {
  Column,
  ColumnOption,
  CustomAction,
  CustomTableAction,
  DataTableComponent,
  EmptyTableComponent,
  ResultFieldMultiFilterElement,
  ResultFieldOption,
  FilterElementSize,
  AbstractFilterElement,
} from '@nimmsta/ng-commons-lib/data-table';
import {
  DataConnection,
  DataConnectionType,
  Job,
  Pageable,
} from '@nimmsta/ng-commons-lib/models';
import { DateTimePipe, StatusBadgePipe } from '@nimmsta/ng-commons-lib/pipes';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { InputWithErrorComponent } from '@nimmsta/ng-commons-lib/forms';
import { ResultFieldsService } from '@nimmsta/ng-commons-lib/services/result-fields';
import { TableConfigurationService } from '@nimmsta/ng-commons-lib/services/table-configuration';
import { FormControl, Validators } from '@angular/forms';
import { CsvJobAction } from './csvJobAction';
import { CsvJobPriorityAction } from './csvJobPriorityAction';
import { ListEndpointDataSource } from '../services/list-endpoint.datasource';
import { DataConnectionService } from '../services/dataConnection.service';
import { JobService } from '../services/job.service';
import { TranslatePipe } from '@ngx-translate/core';
import { NamePipe } from './name.pipe';
import { PriorityIconPipe } from './priority-icon.pipe';

@Component({
  selector: 'app-open-job-table',
  templateUrl: 'job-open-table.component.html',
  styleUrls: ['./job-open-table.component.scss'],
  imports: [
    TranslatePipe,
    TranslatePipe,
    DataTableComponent,
    EmptyTableComponent,
    InputWithErrorComponent,
    ErrorComponent,
  ],
  providers: [
    StatusBadgePipe,
    DateTimePipe,
    NamePipe,
    PriorityIconPipe,
  ],
})
export class JobOpenTableComponent implements OnInit {
  private authorizationService = inject(AuthorizationService);
  private dataConnectionService = inject(DataConnectionService);
  private jobService = inject(JobService);
  private router = inject(Router);
  private statusBadge = inject(StatusBadgePipe);
  private dateTimePipe = inject(DateTimePipe);
  private namePipe = inject(NamePipe);
  private toPriorityPipe = inject(PriorityIconPipe);
  private tableConfigService = inject(TableConfigurationService);
  private resultFieldsService = inject(ResultFieldsService);

  @ViewChild(DataTableComponent)
  private tableComponent!: DataTableComponent<Job>;

  @Input()
  customActions: CustomAction<Job>[] = [];

  @Input()
  customTableActions: CustomTableAction<Job>[] = [];

  jobSource!: ListEndpointDataSource;

  error?: HttpErrorResponse;

  dataConnections = signal<DataConnection[]>([]);

  connectionControl!: FormControl;

  LAST_DATASOURCE_KEY = 'lastDataSource';

  DataConnectionType = DataConnectionType;

  searchEnabled = true;

  customFieldOptions: ColumnOption[] = [];

  filterElements: AbstractFilterElement[] = [];

  activeColumns: Column[] = [];

  private savedVisibleColumns: string[] | null = null;

  canWriteJobs = false;

  canUploadJobs = false;

  public get connectionValues(): string[] {
    return this.dataConnections().map((d) => d.identifier);
  }

  public get connectionNames(): string[] {
    return this.dataConnections().map((d) => d.name);
  }

  get baseColumns(): Column[] {
    return [
      new Column('name', 'job.name', { sortable: true }),
      new Column('status', 'job.status', {
        pipe: this.statusBadge,
        sortable: true,
      }),
      new Column('dataConnection.name', 'dataconnection.singular', {
        sortable: true,
      }),
      new Column('createdAt', 'job.createdAt', {
        pipe: this.dateTimePipe,
        sortable: true,
      }),
      new Column('lastUpdatedAt', 'job.lastUpdatedAt', {
        pipe: this.dateTimePipe,
        sortable: true,
        invertSorting: true,
      }),
      new Column('lastWorker', 'job.lastWorker', {
        pipe: this.namePipe,
        sortable: false,
      }),
    ];
  }

  get columns(): Column[] {
    const configuredColumns =
      this.activeColumns.length > 0 ? this.activeColumns : this.baseColumns;
    return this.withOpenJobPriorityColumn(configuredColumns);
  }

  ngOnInit(): void {
    this.canWriteJobs = this.authorizationService.hasPermission('job.write');
    this.canUploadJobs = this.authorizationService.hasPermission('job.upload');
    this.connectionControl = new FormControl(null, Validators.required);
    this.connectionControl.valueChanges.subscribe((value) =>
      this.changeDatasource(value),
    );
    this.dataConnectionService
      .list({
        filter: null,
        sortColumn: 'name',
        pageSize: 9999,
      })
      .subscribe((page: Pageable<DataConnection>) => {
        this.dataConnections.set(
          page.content.filter(
            (d) =>
              (d.configuration.jobListEndpoint &&
                d.configuration.jobListEndpoint !== '') ||
              d.dataConnectionHandler === DataConnectionType.CSVDataConnection,
          ),
        );
        const lastDataSourceIdentifier = localStorage.getItem(
          this.LAST_DATASOURCE_KEY,
        );
        let connection = this.dataConnections().find(
          (d) => d.identifier === lastDataSourceIdentifier,
        );
        if (!connection) {
          localStorage.removeItem(this.LAST_DATASOURCE_KEY);
        }
        if (!connection && this.dataConnections().length > 0) {
          connection = this.dataConnections()[0];
        }
        this.connectionControl.patchValue(connection?.identifier);
      });
    this.jobSource = new ListEndpointDataSource(this.jobService, this.dataConnectionService);
    this.loadColumnConfiguration();
    if (this.canWriteJobs) {
      const priorityAction = new CsvJobPriorityAction('jobs.add-priority');
      this.customActions.push(priorityAction);
      priorityAction.subscribe((id) => this.togglePriority(id));
      const cancelAction = new CsvJobAction('job.cancel');
      this.customActions.push(cancelAction);
      cancelAction.subscribe((id) => this.cancel(id));
      const deleteAction = new CsvJobAction('delete', true);
      this.customActions.push(deleteAction);
      deleteAction.subscribe((id) => this.delete(id));
    }
    if (this.canUploadJobs) {
      const addJobAction = new CustomTableAction('job.add_jobs', 'work');
      this.customTableActions.push(addJobAction);
      addJobAction.subscribe(() => this.emptyButtonClick());
    }
  }

  load(): void {
    if (this.jobSource.dataConnection) {
      this.tableComponent.loadDataPage(true);
    }
  }

  show(id: string): void {
    const job = this.getSelectedObject(id)!;
    if (
      job.dataConnection?.dataConnectionHandler ===
      DataConnectionType.CSVDataConnection
    ) {
      this.router.navigate(['/jobs', id]);
    }
  }

  delete(id: string): void {
    const job = this.getSelectedObject(id)!;
    this.jobService.delete(job).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  togglePriority(id: string): void {
    const job = this.getSelectedObject(id)!;
    if (job.priorityDate) {
      this.jobService.removePriority(job).subscribe({
        next: () => {
          this.tableComponent.loadDataPage();
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
        },
      });
    } else {
      this.jobService.addPriority(job).subscribe({
        next: () => {
          this.tableComponent.loadDataPage();
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
        },
      });
    }
  }

  cancel(id: string): void {
    const job = this.getSelectedObject(id)!;
    this.jobService.cancel(job).subscribe({
      next: () => {
        this.tableComponent.loadDataPage();
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
      },
    });
  }

  getSelectedObject(id: string): Job | undefined {
    return this.jobSource.data?.content.filter((d) => id === d.id).pop();
  }

  emptyButtonClick(): void {
    this.router.navigate(['/dataconnection']);
  }

  changeDatasource(identifier: string): void {
    localStorage.setItem(this.LAST_DATASOURCE_KEY, identifier);
    this.tableComponent?.clearAllFilters();
    this.jobSource.reset();
    const connection = this.dataConnections().find(
      (d) => d.identifier === identifier,
    );
    this.jobSource.dataConnection = connection;
    this.load();
    if (connection) {
      this.loadDataFields([connection.id]);
    }
  }

  private loadColumnConfiguration(): void {
    this.tableConfigService
      .getConfiguration('jobs')
      .subscribe((config) => {
        if (config?.visibleColumns) {
          this.savedVisibleColumns = config.visibleColumns;
          this.updateActiveColumns(config.visibleColumns);
        }
      });
  }

  private loadDataFields(dataConnectionIds?: string[]): void {
    this.resultFieldsService
      .discoverDataFields(dataConnectionIds)
      .subscribe((response) => {
        this.customFieldOptions = response.fields.map((field) => ({
          key: `data.${field.key}`,
          label: field.key,
          selected:
            this.savedVisibleColumns?.includes(`data.${field.key}`) ?? false,
          isCustom: true,
        }));

        const dataFieldOptions: ResultFieldOption[] = response.fields.map((field) => ({
          key: `data.${field.key}`,
          label: field.key,
        }));

        const existingIndex = this.filterElements.findIndex(
          (filter) => filter.filterKey === 'dataField',
        );

        if (dataFieldOptions.length === 0) {
          if (existingIndex >= 0) {
            this.filterElements.splice(existingIndex, 1);
          }
          return;
        }

        const dataFieldFilter = new ResultFieldMultiFilterElement(
          'filter.result-field.label',
          'dataField',
          dataFieldOptions,
          FilterElementSize.EXTRA_LARGE,
          false,
          'filter.result-field.select-field',
          'filter.result-field.enter-value',
        );

        if (existingIndex >= 0) {
          this.filterElements[existingIndex] = dataFieldFilter;
        } else {
          this.filterElements.push(dataFieldFilter);
        }
      });
  }

  private updateActiveColumns(visibleColumns: string[]): void {
    const baseColumnMap = new Map(
      this.baseColumns.map((col) => [col.displayedProperty, col]),
    );

    const uniqueVisibleColumns = [...new Set(visibleColumns)];

    this.activeColumns = uniqueVisibleColumns
      .map((key) => {
        if (baseColumnMap.has(key)) {
          return baseColumnMap.get(key)!;
        }

        if (key.startsWith('data.')) {
          const fieldName = key.substring(5);
          return new Column(key, fieldName, {
            sortable: false,
            translateHeader: false,
          });
        }

        return null;
      })
      .filter((col): col is Column => col !== null);
  }

  private withOpenJobPriorityColumn(columns: Column[]): Column[] {
    return [
      ...columns.filter(
        (column) => column.displayedProperty !== 'priorityDate',
      ),
      new Column('priorityDate', 'job.priority', {
        pipe: this.toPriorityPipe,
      }),
    ];
  }

  onColumnConfigurationChanged(visibleColumns: string[]): void {
    this.savedVisibleColumns = visibleColumns;
    this.updateActiveColumns(visibleColumns);
  }
}
